import React, { useEffect, useState, useRef } from 'react';
import ColorPicker from '../ColorPicker/ColorPicker';
import { ColorSquare, ColorSquaresWrapper, PopoverWrapper, Logo, Square, BottomDiv } from './styles';
import { Mode } from '../../pages/settings/brandDomain/styles';
import { useMobileDevice } from '../../global/useMobile';

interface BrandColorsProps {
  lightModeColor: string;
  darkModeColor: string;
  onChange: (field: string, hex: string) => void;
}

type Placement = 'top' | 'bottom' | 'left' | 'right';

const BrandColors: React.FC<BrandColorsProps> = ({ lightModeColor, darkModeColor, onChange }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [activeColorPicker, setActiveColorPicker] = useState<'light' | 'dark' | null>(null);
  const [popoverStyle, setPopoverStyle] = useState<{
    top: number;
    left: number;
    placement: Placement;
    arrowLeft: string;
    arrowTop: string;
  }>({ top: 0, left: 0, placement: 'bottom', arrowLeft: '50%', arrowTop: '-12px' });
  const lightRef = useRef<HTMLDivElement>(null);
  const darkRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const triggerRefs = { light: lightRef, dark: darkRef };
  const popoverRef = useRef<HTMLDivElement>(null);
  const isMobile = useMobileDevice();

  const handleSquareClick = (mode: 'light' | 'dark') => {
    setActiveColorPicker(mode);
  };

  // Calculate popover position and placement
  useEffect(() => {
    if (!activeColorPicker) return;
    const triggerRef = triggerRefs[activeColorPicker];
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;

    setTimeout(() => {
      const triggerRect = triggerEl.getBoundingClientRect();
      const popoverEl = popoverRef.current;
      if (!popoverEl) return;
      const popoverRect = popoverEl.getBoundingClientRect();
      const spacing = isMobile ? 10 : 15;
      let placement: Placement = 'bottom';
      // Default: below, centered
      let top = triggerRect.bottom + spacing;
      let left = triggerRect.left + (triggerRect.width - (isMobile ? 65 : 75)) / 2;
      let arrowLeft = '50%';
      let arrowTop = '-12px';

      // Flip above if not enough space below
      if (
        window.innerHeight - triggerRect.bottom < popoverRect.height + spacing &&
        triggerRect.top > popoverRect.height + spacing
      ) {
        placement = 'top';
        top = triggerRect.top - popoverRect.height - spacing;
        arrowTop = '100%';
      }

      // Horizontal overflow handling
      let popoverLeft = left;
      if (left < spacing) {
        popoverLeft = spacing;
      }
      if (left + popoverRect.width > window.innerWidth - spacing) {
        popoverLeft = window.innerWidth - spacing - popoverRect.width;
      }

      // Arrow should always point to the center of the square
      arrowLeft = `${triggerRect.left - popoverLeft + triggerRect.width / 2}px`;

      setPopoverStyle({ top, left: popoverLeft, placement, arrowLeft, arrowTop });
    }, 0);
  }, [activeColorPicker, isMobile, triggerRefs]);

  // Close popover on outside click
  useEffect(() => {
    if (!activeColorPicker) return;
    const handleClickOutside = (event: MouseEvent) => {
      const popoverEl = popoverRef.current;
      if (popoverEl && !popoverEl.contains(event.target as Node)) {
        setActiveColorPicker(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeColorPicker]);

  const handleColorChange = (color: string) => {
    if (activeColorPicker === 'light') {
      onChange('primaryColor', color);
    } else if (activeColorPicker === 'dark') {
      onChange('primaryDarkColor', color);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <ColorSquaresWrapper>
        <Square>
          <Logo>
            <ColorSquare
              ref={triggerRefs.light}
              color={lightModeColor}
              onClick={() => handleSquareClick('light')}
              isActive={activeColorPicker === 'light'}
            />
          </Logo>
          <BottomDiv>
            <p>Color</p>
            <Mode>Light Mode</Mode>
          </BottomDiv>
        </Square>
        <Square>
          <Logo>
            <ColorSquare
              ref={triggerRefs.dark}
              color={darkModeColor}
              onClick={() => handleSquareClick('dark')}
              isActive={activeColorPicker === 'dark'}
            />
          </Logo>
          <BottomDiv>
            <p>Color</p>
            <Mode>Dark Mode</Mode>
          </BottomDiv>
        </Square>
        {activeColorPicker && (
          <PopoverWrapper
            ref={popoverRef}
            placement={popoverStyle.placement}
            arrowLeft={popoverStyle.arrowLeft}
            arrowTop={popoverStyle.arrowTop}
            style={{
              top: popoverStyle.top,
              left: popoverStyle.left
            }}
            className='color-picker-popover'>
            <ColorPicker
              value={activeColorPicker === 'light' ? lightModeColor : darkModeColor}
              onChange={handleColorChange}
            />
          </PopoverWrapper>
        )}
      </ColorSquaresWrapper>
    </div>
  );
};

export default BrandColors;
